import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { ApiError } from 'types';
import { isExpectedApiError } from 'utils/apiError';

const parseSampleRate = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(Math.max(parsed, 0), 1);
};

const sentryTracesSampleRate = parseSampleRate(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  0.05
);
const isSentryEnabled =
  import.meta.env.MODE === 'production' &&
  import.meta.env.VITE_SENTRY_ENABLED !== 'false';
const reportedApiErrors = new WeakSet<ApiError>();

Sentry.init({
  dsn: 'https://70e4e884b1a54f7d8948a538f9c494ac@o1141627.ingest.sentry.io/4504733743775744',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  enabled: isSentryEnabled,
  environment: import.meta.env.MODE,
  tracesSampleRate: sentryTracesSampleRate,
  release: `${import.meta.env.VITE_REPOSITORY}@${import.meta.env.VITE_IMAGE_TAG}`,
  beforeSend(event, hint) {
    const originalException = hint.originalException;
    if (isExpectedApiError(originalException)) return null;
    if (!(originalException instanceof ApiError)) return event;

    if (originalException.statusCode < 500) return null;
    if (reportedApiErrors.has(originalException)) return null;
    reportedApiErrors.add(originalException);

    const { errorCode, failureKind, method, url } =
      originalException.requestContext;
    event.tags = {
      ...event.tags,
      'api.endpoint': url ?? 'unknown',
      'api.error_code': errorCode ?? 'unknown',
      'api.failure_kind': failureKind ?? 'unknown',
      'api.method': method ?? 'unknown',
      'api.status_code': `${originalException.statusCode}`,
    };
    event.contexts = {
      ...event.contexts,
      api_request: {
        endpoint: url ?? 'unknown',
        error_code: errorCode ?? 'unknown',
        failure_kind: failureKind ?? 'unknown',
        method: method ?? 'unknown',
        status_code: originalException.statusCode,
      },
    };
    event.fingerprint = [
      'api-error',
      failureKind ?? 'unknown-failure',
      method ?? 'unknown-method',
      url ?? 'unknown-endpoint',
      `${originalException.statusCode}`,
    ];

    return event;
  },
});

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
